import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePublicPetitionWizardContext } from '../../../hooks/useCaseRequestWizard';
import type { PublicPetitionWizardDraft } from '../../../types/publicPetitionCaseRequest';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { PublicPetitionWizardActions } from './PublicPetitionWizardActions';
import {
  publicPetitionWizardPath,
  PublicPetitionWizardShell,
  FieldError,
  inpClass } from './publicPetitionWizardShell';
import { usePublicPetitionLookups } from './usePublicPetitionLookups';
import { validatePublicPetitionStep } from './publicPetitionStepValidation';
import { useWizardFieldLabels } from '../useWizardFieldLabels';
import { useCaseCategorization } from './useCaseCategorization';

const MAX_DESC = 2000;
const MAX_REF = 255;

const toOpts = (items: { id: string; name: string }[]) => items.map((x) => ({ id: x.id, label: x.name }));

function lookupName(items: { id: string; name: string }[], id: string): string {
  return items.find((x) => x.id === id)?.name ?? '';
}

function mergeLookupItems(
  items: { id: string; name: string }[],
  selectedId: string,
  selectedName: string,
): { id: string; name: string }[] {
  if (!selectedId || items.some((x) => x.id === selectedId)) return items;
  return [...items, { id: selectedId, name: selectedName || selectedId }];
}

export default function PublicPetitionStep1CaseOverviewPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = usePublicPetitionWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const lookups = usePublicPetitionLookups('step1', draft.provinceId);
  const cat = useCaseCategorization(draft.fileCategoryId, draft.subFileCategoryId);

  const provinceOptions = useMemo(
    () => toOpts(mergeLookupItems(lookups.provinces, draft.provinceId, draft.provinceName)),
    [draft.provinceId, draft.provinceName, lookups.provinces],
  );
  const districtOptions = useMemo(
    () => toOpts(mergeLookupItems(lookups.districts, draft.districtId, draft.districtName)),
    [draft.districtId, draft.districtName, lookups.districts],
  );

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  useEffect(() => {
    if (draft.hasConnectedMatters === 'yes' || draft.hasConnectedMatters === 'no') return;
    setDraft((d) => ({ ...d, hasConnectedMatters: 'no' }));
  }, [draft.hasConnectedMatters, setDraft]);

  const validate = (): boolean => {
    const selectedFileCategory = cat.fileCategories.find((c) => c.id === draft.fileCategoryId);
    const { valid, errors: nextErrors, specs } = validatePublicPetitionStep(1, draft, {
      subCategories: cat.subCategories,
      loadingSubCategories: cat.loadingSubCategories,
      fileCategoryCode: selectedFileCategory?.code });
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  const next = () => {
    if (!validate()) return;
    nav(publicPetitionWizardPath(2, draft.draftRequestId));
  };

  return (
    <PublicPetitionWizardShell
      currentStep={1}
      draft={draft}
      setDraft={setDraft}
      categorizationEditable
      categorizationErrors={errors}
      onClearCategorizationError={clearErr}
    >
      <SectionCard title={f.sectionCaseIntakeDetails}>
        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>
            {f.description} <span className="req">*</span>
          </label>
          <textarea
            id="pp-description"
            className={inpClass(Boolean(errors.description))}
            rows={4}            value={draft.description}
            onChange={(e) => {
              setDraft((d: PublicPetitionWizardDraft) => ({ ...d, description: maxLen('description', e.target.value, MAX_DESC) }));
            }}
            placeholder={f.briefDescriptionOfCase}
          />
          <FieldError message={errors.description} />
        </div>

        <div className="ccr-fg" style={{ marginBottom: 14 }} id="pp-caseMatter">
          <label>
            {f.caseMatter} <span className="req">*</span>
          </label>
          <div className="ccr-radio-row">
            {(['CRIMINAL', 'CIVIL'] as const).map((v) => (
              <label key={v} className="ccr-radio">
                <input
                  type="radio"
                  name="pp-caseMatter"
                  checked={draft.caseMatter === v}
                  onChange={() => {
                    setDraft((d: PublicPetitionWizardDraft) => ({ ...d, caseMatter: v }));
                    clearErr('caseMatter');
                  }}
                />
                {v === 'CRIMINAL' ? f.criminalMatter : f.civilMatter}
              </label>
            ))}
          </div>
          <FieldError message={errors.caseMatter} />
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.province} <span className="req">*</span>
            </label>
            <WizardSelect
              id="pp-province"
              hasError={Boolean(errors.provinceId)}
              value={draft.provinceId}
              disabled={lookups.loading}
              placeholder={f.selectProvince}
              options={provinceOptions}
              onChange={(id) => {
                setDraft((d: PublicPetitionWizardDraft) => ({
                  ...d,
                  provinceId: id,
                  provinceName: lookupName(lookups.provinces, id),
                  districtId: '',
                  districtName: '' }));
                clearErr('provinceId');
              }}
            />
            <FieldError message={errors.provinceId} />
          </div>
          <div className="ccr-fg">
            <label>{f.district}</label>
            <WizardSelect
              id="pp-district"
              hasError={Boolean(errors.districtId)}
              value={draft.districtId}
              disabled={!draft.provinceId || lookups.loading}
              placeholder={f.selectDistrict}
              options={districtOptions}
              onChange={(id) => {
                setDraft((d: PublicPetitionWizardDraft) => ({
                  ...d,
                  districtId: id,
                  districtName: lookupName(lookups.districts, id) }));
                clearErr('districtId');
              }}
            />
            <FieldError message={errors.districtId} />
          </div>
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.yourReferenceNumber} <span className="req">*</span>
            </label>
            <input
              id="pp-yourRef"
              className={inpClass(Boolean(errors.yourReferenceNumber))}
              type="text"              value={draft.yourReferenceNumber}
              onChange={(e) => {
                setDraft((d: PublicPetitionWizardDraft) => ({
                  ...d,
                  yourReferenceNumber: maxLen('yourReferenceNumber', e.target.value, MAX_REF) }));
              }}
              placeholder={f.enterExternalReferenceNumber}
            />
            <FieldError message={errors.yourReferenceNumber} />
          </div>
          <div className="ccr-fg">
            <label>{f.otherExternalReferences}</label>
            <input
              id="pp-otherRef"
              className={inpClass(Boolean(errors.otherExternalReferences))}
              type="text"              value={draft.otherExternalReferences}
              onChange={(e) => {
                setDraft((d: PublicPetitionWizardDraft) => ({
                  ...d,
                  otherExternalReferences: maxLen('otherExternalReferences', e.target.value, MAX_REF) }));
              }}
              placeholder={f.commaSeparatedReferences}
            />
            <FieldError message={errors.otherExternalReferences} />
          </div>
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.caseNumber}</label>
            <input
              id="pp-caseNumber"
              className={inpClass(Boolean(errors.caseNumber))}
              type="text"              value={draft.caseNumber}
              onChange={(e) => {
                setDraft((d: PublicPetitionWizardDraft) => ({
                  ...d,
                  caseNumber: maxLen('caseNumber', e.target.value, MAX_REF) }));
              }}
              placeholder={f.courtReferenceNumber}
            />
            <FieldError message={errors.caseNumber} />
          </div>
        </div>

        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>
            {f.connectedMatters} <span className="req">*</span>
          </label>
          <div className="ccr-radio-row" id="pp-connected">
            {(['yes', 'no'] as const).map((v) => (
              <label key={v} className="ccr-radio">
                <input
                  type="radio"
                  name="pp-connected"
                  checked={draft.hasConnectedMatters === v}
                  onChange={() => {
                    setDraft((d: PublicPetitionWizardDraft) => ({
                      ...d,
                      hasConnectedMatters: v,
                      ...(v === 'no'
                        ? {
                            connectedCaseNumber: '',
                            connectedAgdFileRefAvailable: false,
                            connectedAgdFileRef: '' }
                        : {
                            connectedAgdFileRefAvailable: false,
                            connectedAgdFileRef: '' }) }));
                    clearErr('hasConnectedMatters');
                    clearErr('connectedAgdFileRef');
                  }}
                />
                {v === 'yes' ? f.yes : f.no}
              </label>
            ))}
          </div>
          <FieldError message={errors.hasConnectedMatters} />
        </div>

        {draft.hasConnectedMatters === 'yes' ? (
          <div className="ccr-fr c2" style={{ marginBottom: 0 }}>
            <div className="ccr-fg">
              <label>
                {f.connectedCaseNumber} <span className="req">*</span>
              </label>
              <input
                id="pp-connectedCase"
                className={inpClass(Boolean(errors.connectedCaseNumber))}
                type="text"                value={draft.connectedCaseNumber}
                onChange={(e) => {
                  setDraft((d: PublicPetitionWizardDraft) => ({
                    ...d,
                    connectedCaseNumber: maxLen('connectedCaseNumber', e.target.value, MAX_REF) }));
                }}
                placeholder={f.enterConnectedCaseNumber}
              />
              <FieldError message={errors.connectedCaseNumber} />
            </div>
            <div className="ccr-fg">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  marginBottom: 6 }}
              >
                <label style={{ marginBottom: 0 }}>
                  {f.connectedAgdFileRef}
                  {draft.connectedAgdFileRefAvailable ? <span className="req"> *</span> : null}
                </label>
                <label className="ccr-check" style={{ margin: 0, whiteSpace: 'nowrap' }}>
                  <input
                    id="pp-connectedAgdAvailable"
                    type="checkbox"
                    checked={draft.connectedAgdFileRefAvailable}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setDraft((d: PublicPetitionWizardDraft) => ({
                        ...d,
                        connectedAgdFileRefAvailable: checked,
                        connectedAgdFileRef: checked ? d.connectedAgdFileRef : '' }));
                      clearErr('connectedAgdFileRef');
                    }}
                  />
                  <span>{f.available}</span>
                </label>
              </div>
              <input
                id="pp-connectedAgd"
                className={inpClass(Boolean(errors.connectedAgdFileRef))}
                type="text"                disabled={!draft.connectedAgdFileRefAvailable}
                value={draft.connectedAgdFileRef}
                onChange={(e) => {
                  setDraft((d: PublicPetitionWizardDraft) => ({
                    ...d,
                    connectedAgdFileRef: maxLen('connectedAgdFileRef', e.target.value, MAX_REF) }));
                }}
                placeholder={f.enterConnectedAgdFileRef}
              />
              <p className="up-hint" style={{ marginTop: 6 }}>
                {f.connectedAgdHint}
              </p>
              <FieldError message={errors.connectedAgdFileRef} />
            </div>
          </div>
        ) : null}
      </SectionCard>

      <PublicPetitionWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={1}
        onNext={next}
        validateBeforeSave={validate}
      />
    </PublicPetitionWizardShell>
  );
}
